import { supabase } from './supabase';
import type { AppSetting } from '../types/domain.types';

/**
 * Settings Service
 * Handles fetching and managing application settings
 */

export const settingsService = {
  /**
   * Fetch a specific setting by key
   */
  async getSetting(key: string): Promise<AppSetting | null> {
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .eq('setting_key', key)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      console.error('Error fetching setting:', error);
      throw new Error(error.message);
    }

    return data as AppSetting;
  },

  /**
   * Fetch all settings
   */
  async getAllSettings(): Promise<AppSetting[]> {
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .order('setting_key', { ascending: true });

    if (error) {
      console.error('Error fetching settings:', error);
      throw new Error(error.message);
    }

    return data as AppSetting[];
  },

  /**
   * Get boolean setting value with default fallback
   */
  async getBooleanSetting(key: string, defaultValue = false): Promise<boolean> {
    const setting = await this.getSetting(key);
    if (!setting) return defaultValue;
    return setting.setting_value.toLowerCase() === 'true';
  },

  /**
   * Update a setting value
   */
  async updateSetting(key: string, value: string): Promise<AppSetting> {
    const { data, error} = await supabase
      .from('app_settings')
      // @ts-expect-error - Supabase type mismatch with generated types
      .update({ setting_value: value })
      .eq('setting_key', key)
      .select()
      .single();

    if (error) {
      console.error('Error updating setting:', error);
      throw new Error(error.message);
    }

    return data as AppSetting;
  },

  /**
   * Get all feature flags (settings with key prefix "feature_")
   */
  async getFeatureFlags(): Promise<Record<string, boolean>> {
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .like('setting_key', 'feature_%');

    if (error) {
      console.error('Error fetching feature flags:', error);
      throw new Error(error.message);
    }

    const flags: Record<string, boolean> = {};
    data.forEach((setting: AppSetting) => {
      flags[setting.setting_key] = setting.setting_value.toLowerCase() === 'true';
    });

    return flags;
  },

  /**
   * Get all report visibility settings (settings with key prefix "report_")
   */
  async getReportSettings(): Promise<Record<string, boolean>> {
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .like('setting_key', 'report_%');

    if (error) {
      console.error('Error fetching report settings:', error);
      throw new Error(error.message);
    }

    const reports: Record<string, boolean> = {};
    data.forEach((setting: AppSetting) => {
      reports[setting.setting_key] = setting.setting_value.toLowerCase() === 'true';
    });

    return reports;
  },

  /**
   * Toggle a feature flag
   */
  async toggleFeatureFlag(key: string, enabled: boolean): Promise<AppSetting> {
    return this.updateSetting(key, enabled ? 'true' : 'false');
  },

  /**
   * Toggle a report visibility setting
   */
  async toggleReportSetting(key: string, visible: boolean): Promise<AppSetting> {
    return this.updateSetting(key, visible ? 'true' : 'false');
  },

  /**
   * Create a new app setting
   */
  async createSetting(key: string, value: string, description?: string): Promise<AppSetting> {
    const { data, error } = await supabase
      .from('app_settings')
      // @ts-expect-error - Database types not regenerated yet
      .insert({
        setting_key: key,
        setting_value: value,
        description: description ?? null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating setting:', error);
      throw new Error(error.message);
    }

    return data as AppSetting;
  },

  /**
   * Delete an app setting
   */
  async deleteSetting(key: string): Promise<void> {
    const { error } = await supabase
      .from('app_settings')
      .delete()
      .eq('setting_key', key);

    if (error) {
      console.error('Error deleting setting:', error);
      throw new Error(error.message);
    }
  },
};
